import type { GpsPoint } from '../domain/telemetry/processed/GpsPoint'

export interface XyzReading {
  x: number
  y: number
  z: number
  timestamp: Date
}

/**
 * Average all readings whose timestamp falls in
 * `(windowStartExclusiveMs, windowEndInclusiveMs]`, resuming the scan from
 * `startIndex` so a full GPS-point pass over the (timestamp-sorted) stream
 * stays a single O(n) sweep.
 *
 * Why: IMU streams are often sampled far above GPS rate (GoPro ACCL/GYRO
 * ~200Hz vs GPS 10Hz). Picking a single nearest sample per GPS point aliases
 * high-frequency engine/road vibration into the fused signal. Averaging
 * every sample that actually falls in a GPS tick's window cancels the
 * vibration (its mean is ~0) while keeping the real low-frequency signal;
 * for a gyroscope the window mean times the tick interval is exactly the
 * integrated angle over the tick.
 *
 * For an equal-rate stream (one sample per GPS point, timestamps aligned -
 * RaceBox, and all previously-ingested mobile data) the window always
 * contains exactly that one sample, so the result equals a nearest-sample
 * pick: no behaviour change for equal-rate data.
 *
 * Empty window (a sensor gap, or a duplicate window end): HOLD the last
 * sample at or before the window end - the same fallback the mobile GoPro
 * serializer uses (`averageInWindow` in goProCsvSerializer.ts). Never reach
 * forward to a nearest sample in the future: at a second-resolution
 * timestamp several GPS rows share one value, and every window after the
 * first is then `(T, T]` - empty - where the honest answer is the reading
 * recorded AT T, not the next second's. The one intentional difference from
 * the mobile serializer is at the head, where no earlier sample exists: this
 * helper holds `readings[0]` (the fusion needs a reading for every GPS point
 * it processes) where the serializer emits empty CSV cells.
 *
 * The averaged reading carries every other field (unit, cts, ...) of the
 * last sample in the window.
 */
export function averageReadingsInWindow<T extends XyzReading>(
  readings: T[],
  windowStartExclusiveMs: number,
  windowEndInclusiveMs: number,
  startIndex: number
): { reading: T | undefined; index: number } {
  let i = startIndex
  while (
    i < readings.length &&
    readings[i].timestamp.getTime() <= windowStartExclusiveMs
  ) {
    i++
  }

  let sumX = 0
  let sumY = 0
  let sumZ = 0
  let j = i
  while (
    j < readings.length &&
    readings[j].timestamp.getTime() <= windowEndInclusiveMs
  ) {
    sumX += readings[j].x
    sumY += readings[j].y
    sumZ += readings[j].z
    j++
  }

  if (j > i) {
    const count = j - i
    return {
      reading: {
        ...readings[j - 1],
        x: sumX / count,
        y: sumY / count,
        z: sumZ / count,
        timestamp: new Date(windowEndInclusiveMs),
      },
      index: j,
    }
  }

  if (readings.length === 0) return { reading: undefined, index: 0 }
  return { reading: readings[i > 0 ? i - 1 : 0], index: i }
}

/**
 * Exclusive start of the FIRST GPS point's averaging window.
 *
 * Every later window opens at the previous GPS timestamp, but point 0 has no
 * predecessor. Opening it at -Infinity folds EVERY sample ever recorded
 * before the first GPS fix into point 0, and devices that start their IMU
 * before GPS lock (GoPro, RaceBox) can sit stationary for minutes first -
 * enough to drag point 0 far off its true value. Clamp the window to one
 * median GPS interval, the same span every other point gets.
 *
 * Returns -Infinity (the unclamped behaviour) when no interval can be
 * established: fewer than two points, or every point sharing one timestamp.
 * With no scale to clamp to, widening is the safer failure.
 */
export function firstWindowStartExclusiveMs(gpsPoints: GpsPoint[]): number {
  if (gpsPoints.length === 0) return -Infinity

  const intervals: number[] = []
  for (let i = 1; i < gpsPoints.length; i++) {
    const delta =
      gpsPoints[i].timestamp.getTime() - gpsPoints[i - 1].timestamp.getTime()
    if (delta > 0) intervals.push(delta)
  }
  if (intervals.length === 0) return -Infinity

  intervals.sort((a, b) => a - b)
  const median = intervals[Math.floor(intervals.length / 2)]
  return gpsPoints[0].timestamp.getTime() - median
}

/**
 * One reading per GPS point: the window average of `readings` over
 * `(previous GPS timestamp, this GPS timestamp]`, stamped on the GPS tick.
 * `undefined` only when the stream is empty.
 */
export function reduceReadingsToGpsTicks<T extends XyzReading>(
  readings: T[],
  gpsPoints: GpsPoint[]
): Array<T | undefined> {
  const firstWindowStartMs = firstWindowStartExclusiveMs(gpsPoints)
  let index = 0
  return gpsPoints.map((point, i) => {
    const window = averageReadingsInWindow(
      readings,
      i > 0 ? gpsPoints[i - 1].timestamp.getTime() : firstWindowStartMs,
      point.timestamp.getTime(),
      index
    )
    index = window.index
    return window.reading
  })
}
